/**
 * Run training plan storage — persists to Supabase with localStorage cache.
 *
 * Plans are stored server-side in the `run_plans` table so they survive
 * browser data clears, work across devices, and are accessible to server
 * features (coach chat, weekly reports, cross-domain intelligence).
 *
 * localStorage is used as a fast read cache to avoid a network round-trip
 * on every app load.
 *
 * Smart plan intelligence:
 * - Flexible completion matching (within the same week, not just exact date)
 * - Missed run detection with make-up suggestions
 * - Actual vs planned performance comparison
 * - Weekly mileage progress tracking
 */

import { supabase } from "../lib/supabase";

const PLAN_KEY = "elevate_run_plan";

/** Read the active run plan — fast path from localStorage, fallback to Supabase */
export async function getRunPlan(userId) {
  // 1. Try localStorage cache first (sync, fast)
  try {
    const stored = localStorage.getItem(`${PLAN_KEY}_${userId}`);
    if (stored) return JSON.parse(stored);
  } catch { /* corrupt cache — fall through */ }

  // 2. Fetch from Supabase
  try {
    const { data, error } = await supabase
      .from("run_plans")
      .select("plan_json, race_type, race_date, goal_time, generated_at")
      .eq("user_id", userId)
      .eq("active", true)
      .maybeSingle();
    if (error || !data) return null;
    const plan = {
      plan: data.plan_json,
      race_type: data.race_type,
      race_date: data.race_date,
      goal_time: data.goal_time || null,
      generated_at: data.generated_at,
    };
    // Populate cache for next load
    localStorage.setItem(`${PLAN_KEY}_${userId}`, JSON.stringify(plan));
    return plan;
  } catch {
    return null;
  }
}

/** Save plan to localStorage cache (server save happens in the API endpoint) */
export function saveRunPlan(userId, plan) {
  localStorage.setItem(`${PLAN_KEY}_${userId}`, JSON.stringify(plan));
}

/** Delete the active plan from Supabase + clear localStorage cache */
export async function deleteRunPlan(userId) {
  localStorage.removeItem(`${PLAN_KEY}_${userId}`);
  try {
    await supabase
      .from("run_plans")
      .update({ active: false })
      .eq("user_id", userId)
      .eq("active", true);
  } catch (e) {
    console.error("Failed to deactivate run plan:", e.message);
  }
}

// ─── Helpers ───

const DAY_NAMES_SUN = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAY_NAMES_MON = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function toDateStr(d) {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
}

/** Get the Monday of a given date's week */
function getMonday(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
}

/** Get the actual calendar date for a scheduled run in the current week */
function getScheduledDate(scheduledRun) {
  const dayIndex = DAY_NAMES_MON.indexOf(scheduledRun.day);
  if (dayIndex === -1) return null;
  const monday = getMonday(new Date());
  const d = new Date(monday);
  d.setDate(d.getDate() + dayIndex);
  return d;
}

// ─── Core plan queries ───

/**
 * Get the current week number based on the plan start date.
 * Returns 1-indexed week number.
 */
export function getCurrentWeek(plan) {
  if (!plan?.generated_at) return 1;
  const startMonday = getMonday(new Date(plan.generated_at));
  const nowMonday = getMonday(new Date());
  const weeksDiff = Math.floor((nowMonday - startMonday) / (7 * 86400000));
  return Math.max(1, Math.min(weeksDiff + 1, plan.plan?.total_weeks || 1));
}

/**
 * Get today's scheduled run from the plan.
 */
export function getTodayRun(plan) {
  if (!plan?.plan?.weeks) return null;
  const week = getCurrentWeek(plan);
  const weekData = plan.plan.weeks.find((w) => w.week_number === week);
  if (!weekData) return null;
  const today = DAY_NAMES_SUN[new Date().getDay()];
  return weekData.runs?.find((r) => r.day === today) || null;
}

/**
 * Get this week's runs from the plan.
 */
export function getThisWeekRuns(plan) {
  if (!plan?.plan?.weeks) return [];
  const week = getCurrentWeek(plan);
  const weekData = plan.plan.weeks.find((w) => w.week_number === week);
  return weekData?.runs || [];
}

/**
 * Get this week's scheduled mileage.
 */
export function getWeekMileage(plan) {
  if (!plan?.plan?.weeks) return 0;
  const week = getCurrentWeek(plan);
  const weekData = plan.plan.weeks.find((w) => w.week_number === week);
  return weekData?.total_mileage || 0;
}

/**
 * Compute race countdown.
 */
export function getRaceCountdown(plan) {
  if (!plan?.race_date) return null;
  const race = new Date(plan.race_date);
  const now = new Date();
  const days = Math.ceil((race - now) / 86400000);
  if (days < 0) return null;
  return { days, weeks: Math.floor(days / 7) };
}

// ─── Smart completion matching ───

/** Get all logged runs within the current week (Mon-Sun) */
function getWeekLoggedRuns(loggedRuns) {
  if (!loggedRuns?.length) return [];
  const monday = getMonday(new Date());
  const sunday = new Date(monday);
  sunday.setDate(sunday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);
  return loggedRuns.filter((r) => {
    const d = new Date(r.timestamp);
    return d >= monday && d <= sunday;
  });
}

/**
 * Smart run type similarity — groups compatible run types for flexible matching.
 * e.g., if you do an "Easy" run when "Recovery" was scheduled, that still counts.
 */
function areRunTypesCompatible(scheduledType, loggedType) {
  if (scheduledType === loggedType) return true;
  const groups = [
    ["Easy", "Recovery"],
    ["Tempo", "Race Pace"],
    ["Long Run"],
    ["Intervals"],
  ];
  for (const group of groups) {
    if (group.includes(scheduledType) && group.includes(loggedType)) return true;
  }
  return false;
}

/**
 * Smart completion matching — matches logged runs to scheduled runs within the week.
 *
 * Algorithm:
 * 1. First pass: exact date + type matches (best quality)
 * 2. Second pass: exact date, any run type (you ran on the right day)
 * 3. Third pass: same week + matching type (you did the workout on a different day)
 *
 * Each logged run can only be "claimed" once.
 */
export function getWeekCompletionMap(plan, loggedRuns) {
  const weekRuns = getThisWeekRuns(plan);
  const weekLogged = getWeekLoggedRuns(loggedRuns);
  if (!weekRuns.length) return {};

  // Map: scheduledRun.day -> { completed, loggedRun, matchQuality }
  const map = {};
  const claimedLogIds = new Set();

  const scheduled = weekRuns.filter(
    (r) => r.type !== "Rest" && r.type !== "Cross Train"
  );

  // Pass 1: Exact date + compatible type (best match)
  for (const sr of scheduled) {
    const srDate = getScheduledDate(sr, plan);
    if (!srDate) continue;
    const srDateStr = toDateStr(srDate);

    const match = weekLogged.find((lr) => {
      if (claimedLogIds.has(lr.id)) return false;
      return toDateStr(lr.timestamp) === srDateStr && areRunTypesCompatible(sr.type, lr.runType);
    });

    if (match) {
      claimedLogIds.add(match.id);
      map[sr.day] = { completed: true, loggedRun: match, matchQuality: "exact" };
    }
  }

  // Pass 2: Exact date, any type (ran on the right day, wrong type)
  for (const sr of scheduled) {
    if (map[sr.day]) continue;
    const srDate = getScheduledDate(sr, plan);
    if (!srDate) continue;
    const srDateStr = toDateStr(srDate);

    const match = weekLogged.find((lr) => {
      if (claimedLogIds.has(lr.id)) return false;
      return toDateStr(lr.timestamp) === srDateStr;
    });

    if (match) {
      claimedLogIds.add(match.id);
      map[sr.day] = { completed: true, loggedRun: match, matchQuality: "date" };
    }
  }

  // Pass 3: Same week + compatible type (did the right workout on a different day)
  for (const sr of scheduled) {
    if (map[sr.day]) continue;
    const srDate = getScheduledDate(sr, plan);
    if (!srDate) continue;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    // Only match past scheduled runs (don't pre-claim future ones)
    if (srDate > now) continue;

    const match = weekLogged.find((lr) => {
      if (claimedLogIds.has(lr.id)) return false;
      return areRunTypesCompatible(sr.type, lr.runType);
    });

    if (match) {
      claimedLogIds.add(match.id);
      map[sr.day] = { completed: true, loggedRun: match, matchQuality: "flexible" };
    }
  }

  return map;
}

/**
 * Check if a scheduled run was completed — now uses smart weekly matching.
 */
export function isRunCompleted(scheduledRun, loggedRuns, plan) {
  if (!scheduledRun || scheduledRun.type === "Rest" || scheduledRun.type === "Cross Train") return false;
  if (!loggedRuns?.length) return false;
  const map = getWeekCompletionMap(plan, loggedRuns);
  return !!map[scheduledRun.day]?.completed;
}

/**
 * Get the logged run that matched a scheduled run (for actual vs planned comparison).
 */
export function getMatchedLoggedRun(scheduledRun, loggedRuns, plan) {
  if (!scheduledRun || !loggedRuns?.length) return null;
  const map = getWeekCompletionMap(plan, loggedRuns);
  return map[scheduledRun.day]?.loggedRun || null;
}

// ─── Missed runs & intelligence ───

/**
 * Get missed runs — scheduled runs from earlier this week that weren't completed.
 */
export function getMissedRuns(plan, loggedRuns) {
  const weekRuns = getThisWeekRuns(plan);
  if (!weekRuns.length) return [];

  const map = getWeekCompletionMap(plan, loggedRuns);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const todayIdx = (now.getDay() + 6) % 7; // 0=Mon

  return weekRuns.filter((r) => {
    if (r.type === "Rest" || r.type === "Cross Train") return false;
    const dayIdx = DAY_NAMES_MON.indexOf(r.day);
    // Only past days (not today or future)
    if (dayIdx >= todayIdx) return false;
    return !map[r.day]?.completed;
  });
}

/**
 * Get weekly mileage progress — actual miles logged vs planned this week.
 */
export function getWeekMileageProgress(plan, loggedRuns) {
  const planned = getWeekMileage(plan);
  const weekLogged = getWeekLoggedRuns(loggedRuns);

  const actual = weekLogged.reduce((sum, r) => {
    const dist = r.unit === "km" ? r.distance * 0.621371 : r.distance;
    return sum + (dist || 0);
  }, 0);

  return { planned, actual: Math.round(actual * 10) / 10 };
}

/**
 * Compare actual logged run vs what the plan prescribed.
 * Returns coaching feedback.
 */
export function getRunFeedback(scheduledRun, loggedRun) {
  if (!scheduledRun || !loggedRun) return null;

  const plannedDist = scheduledRun.distance || 0;
  const actualDist = loggedRun.unit === "km"
    ? loggedRun.distance * 0.621371
    : loggedRun.distance;
  const distDiff = actualDist - plannedDist;
  const distPct = plannedDist > 0 ? (distDiff / plannedDist) * 100 : 0;

  const feedback = { plannedDist, actualDist: Math.round(actualDist * 10) / 10, distDiff: Math.round(distDiff * 10) / 10, distPct: Math.round(distPct) };

  if (Math.abs(distPct) <= 10) {
    feedback.verdict = "nailed";
    feedback.message = "Nailed it — right on target.";
  } else if (distPct > 10 && distPct <= 30) {
    feedback.verdict = "over";
    feedback.message = "Went a bit further than planned. Nice effort, just watch recovery.";
  } else if (distPct > 30) {
    feedback.verdict = "way_over";
    feedback.message = "Went well over the plan. Take it easy tomorrow to avoid overtraining.";
  } else if (distPct < -10 && distPct >= -30) {
    feedback.verdict = "under";
    feedback.message = "A little short — still counts. Every mile builds fitness.";
  } else {
    feedback.verdict = "way_under";
    feedback.message = "Came up short today. That's okay, consistency beats perfection.";
  }

  return feedback;
}

/**
 * Get a smart suggestion for what to do today, considering missed runs.
 * Returns: { type: "scheduled" | "makeUp" | "rest" | "bonus", run, message }
 */
export function getTodaySmartSuggestion(plan, loggedRuns) {
  const todayRun = getTodayRun(plan);
  const missed = getMissedRuns(plan, loggedRuns);
  const map = getWeekCompletionMap(plan, loggedRuns);

  // Today is a run day and not yet completed
  if (todayRun && todayRun.type !== "Rest" && todayRun.type !== "Cross Train") {
    const todayDone = map[todayRun.day]?.completed;
    if (!todayDone) {
      if (missed.length > 0) {
        // Today has a run AND there are missed runs — prioritize today's scheduled run
        // but mention the missed one
        const missedType = missed[missed.length - 1]; // most recent miss
        return {
          type: "scheduled",
          run: todayRun,
          missedRun: missedType,
          message: `You missed ${missedType.day}'s ${missedType.type} (${missedType.distance} mi). Focus on today's run — your body adapts to consistency, not perfection.`,
        };
      }
      return { type: "scheduled", run: todayRun, message: null };
    }
    // Today's run is done
    return { type: "completed", run: todayRun, loggedRun: map[todayRun.day]?.loggedRun, message: null };
  }

  // Today is rest/cross-train — but there are missed runs
  if (missed.length > 0 && todayRun?.type === "Rest") {
    const makeUp = missed[missed.length - 1];
    return {
      type: "makeUp",
      run: makeUp,
      originalDay: makeUp.day,
      message: `You missed ${makeUp.day}'s ${makeUp.type}. Want to make it up today? Reduce the distance by ~15% since you're doubling up days.`,
      adjustedDistance: Math.round(makeUp.distance * 0.85 * 10) / 10,
    };
  }

  // Normal rest or cross-train day
  return { type: todayRun?.type === "Cross Train" ? "crossTrain" : "rest", run: todayRun, message: null };
}
